import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Inject, inject, Input, NgZone, OnChanges, OnDestroy, OnInit, PLATFORM_ID, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';

import { LegalDocumentContentService, LegalDocumentType } from './legal-document-content.service';
import { LegalDocumentPdfService } from './legal-document-pdf.service';

interface LegalDocumentSection {
  id: string;
  title: string;
}

interface LegalDocumentSectionRange {
  id: string;
  top: number;
  bottom: number;
}

@Component({
  selector: 'app-legal-document',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  templateUrl: './legal-document.component.html',
  styleUrls: ['./legal-document.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LegalDocumentComponent implements OnInit, OnChanges, OnDestroy {
  @Input() documentType: LegalDocumentType = 'terms';
  @Input() mode: 'page' | 'modal' = 'page';

  year: number = new Date().getFullYear();
  title = '';
  lastUpdated = '';
  lastUpdatedValue = '';
  direction: 'ltr' | 'rtl' = 'ltr';
  documentHtml: SafeHtml | null = null;
  isLoading = false;
  isExportingPdf = false;

  scrollProgress = 0;
  showBackToTop = false;
  tableOfContents: LegalDocumentSection[] = [];
  activeSection = '';

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _scrollHandler: () => void;
  private readonly _resizeHandler: () => void;
  private readonly _isBrowser: boolean;
  private _animationFrameId: number | null = null;
  private _documentHtmlSource = '';
  private _sectionRanges: LegalDocumentSectionRange[] = [];
  private _isScrollListenerAttached = false;
  private _pendingActiveSectionId = '';

  constructor(
    public _translate: TranslateService,
    private readonly _legalDocumentContentService: LegalDocumentContentService,
    private readonly _legalDocumentPdfService: LegalDocumentPdfService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _ngZone: NgZone,
    private readonly _sanitizer: DomSanitizer,
    @Inject(DOCUMENT) private readonly _document: Document,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this._isBrowser = isPlatformBrowser(platformId);
    this._scrollHandler = () => this._scheduleViewportStateUpdate();
    this._resizeHandler = () => {
      this._measureSectionRanges();
      this._scheduleViewportStateUpdate();
    };
  }

  ngOnInit(): void {
    this.loadDocument();
    this._translate.onLangChange
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this.loadDocument());
    this._syncPageModeState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['documentType'] && !changes['documentType'].firstChange) {
      this.loadDocument();
    }

    if (changes['mode'] && !changes['mode'].firstChange) {
      this._syncPageModeState();
    }
  }

  ngOnDestroy(): void {
    this._detachViewportListeners();

    const defaultView = this._document.defaultView;
    if (defaultView && this._animationFrameId !== null) {
      defaultView.cancelAnimationFrame(this._animationFrameId);
    }
  }

  scrollToSection(event: Event, id: string): void {
    event.preventDefault();

    const el = this._document.getElementById(id);
    const defaultView = this._document.defaultView;

    if (el && defaultView) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + defaultView.scrollY - offset;
      defaultView.scrollTo({ top, behavior: 'smooth' });
      this.activeSection = id;
      this._pendingActiveSectionId = id;
      this._cdr.markForCheck();
    }
  }

  scrollToTop(): void {
    this._document.defaultView?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async printDocument(): Promise<void> {
    if (this.isLoading || this.isExportingPdf || !this._documentHtmlSource.trim()) {
      return;
    }

    this.isExportingPdf = true;
    this._cdr.markForCheck();

    try {
      await this._legalDocumentPdfService.exportLegalDocument({
        documentType: this.documentType,
        title: this.title,
        lastUpdated: this.lastUpdated,
        direction: this.direction,
        html: this._documentHtmlSource,
      });
    } catch (error) {
      console.error('Failed to export legal document PDF.', error);
    } finally {
      this.isExportingPdf = false;
      this._cdr.markForCheck();
    }
  }

  private _updateViewportState(): void {
    const defaultView = this._document.defaultView;
    if (!defaultView) {
      return;
    }

    const scrollTop = defaultView.scrollY;
    const viewportHeight = defaultView.innerHeight;
    const documentHeight = this._document.documentElement.scrollHeight;
    const scrollableHeight = documentHeight - viewportHeight;
    const nextScrollProgress = scrollableHeight > 0
      ? Math.min(100, Math.max(0, Math.round((scrollTop / scrollableHeight) * 100)))
      : 0;
    const nextShowBackToTop = scrollTop > 400;
    const nextActiveSection = this._getNextActiveSection(scrollTop, viewportHeight);

    if (
      nextScrollProgress === this.scrollProgress &&
      nextShowBackToTop === this.showBackToTop &&
      nextActiveSection === this.activeSection
    ) {
      return;
    }

    this.scrollProgress = nextScrollProgress;
    this.showBackToTop = nextShowBackToTop;
    this.activeSection = nextActiveSection;
    this._ngZone.run(() => this._cdr.markForCheck());
  }

  private loadDocument(): void {
    const resolved = this._legalDocumentContentService.resolveDocument(
      this.documentType,
      this._translate.getCurrentLang(),
    );

    this.title = resolved.meta.title;
    this.lastUpdated = resolved.meta.lastUpdated;
    this.lastUpdatedValue = this._stripLastUpdatedLabel(resolved.meta.lastUpdated);
    this.direction = resolved.direction;
    this.isLoading = true;
    this._documentHtmlSource = '';
    this.tableOfContents = [];
    this._sectionRanges = [];
    this._pendingActiveSectionId = '';
    this.activeSection = '';
    this._cdr.markForCheck();

    this._legalDocumentContentService.loadDocumentHtml(resolved.path)
      .pipe(take(1))
      .subscribe({
        next: (html: string) => {
          const { safeHtml, sections } = this._processTOC(html);
          this._documentHtmlSource = html;
          this.documentHtml = safeHtml;
          this.tableOfContents = sections;
          this.isLoading = false;
          this._cdr.markForCheck();
          this._scheduleSectionMeasurement();
        },
        error: () => {
          this._documentHtmlSource = '';
          this.documentHtml = null;
          this._sectionRanges = [];
          this.isLoading = false;
          this._cdr.markForCheck();
        },
      });
  }

  private _processTOC(html: string): { safeHtml: SafeHtml; sections: LegalDocumentSection[] } {
    if (!this._isBrowser) {
      return { safeHtml: this._sanitizer.bypassSecurityTrustHtml(html), sections: [] };
    }

    const DomParserCtor = this._document.defaultView?.DOMParser;
    if (!DomParserCtor) {
      return { safeHtml: this._sanitizer.bypassSecurityTrustHtml(html), sections: [] };
    }

    const parser = new DomParserCtor();
    const doc = parser.parseFromString(html, 'text/html');

    // Target h5 headings (Privacy Policy) and bold-underline paragraphs used as section headings (Terms)
    const headings = doc.querySelectorAll('h5, p.text-decoration-underline.fw-bold');
    const sections: LegalDocumentSection[] = [];

    headings.forEach((heading, index) => {
      const id = `ld-section-${index}`;
      heading.setAttribute('id', id);
      const rawTitle = heading.textContent?.trim().replace(/:$/, '') || `Section ${index + 1}`;
      sections.push({ id, title: rawTitle });
    });

    // bypassSecurityTrustHtml preserves id attributes that Angular's default sanitizer would strip
    // This is safe: the HTML comes from static trusted asset files (assets/legal/*.html)
    const safeHtml = this._sanitizer.bypassSecurityTrustHtml(doc.body.innerHTML);
    return { safeHtml, sections };
  }

  private _syncPageModeState(): void {
    if (!this._isBrowser) {
      return;
    }

    const defaultView = this._document.defaultView;
    if (!defaultView) {
      return;
    }

    if (this.mode === 'page') {
      if (!this._isScrollListenerAttached) {
        this._ngZone.runOutsideAngular(() => {
          defaultView.addEventListener('scroll', this._scrollHandler, { passive: true });
          defaultView.addEventListener('resize', this._resizeHandler, { passive: true });
        });
        this._isScrollListenerAttached = true;
      }

      this._scheduleSectionMeasurement();
      return;
    }

    this._detachViewportListeners();

    const shouldMarkForCheck = this.scrollProgress !== 0 || this.showBackToTop || this.activeSection !== '';
    this._sectionRanges = [];
    this._pendingActiveSectionId = '';
    this.scrollProgress = 0;
    this.showBackToTop = false;
    this.activeSection = '';

    if (shouldMarkForCheck) {
      this._cdr.markForCheck();
    }
  }

  private _scheduleSectionMeasurement(): void {
    if (!this._isBrowser || this.mode !== 'page' || this.tableOfContents.length === 0) {
      return;
    }

    this._ngZone.onStable
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._ngZone.runOutsideAngular(() => {
          this._measureSectionRanges();
          this._updateViewportState();
        });
      });
  }

  private _measureSectionRanges(): void {
    const defaultView = this._document.defaultView;
    if (!defaultView) {
      return;
    }

    const sectionStarts = this.tableOfContents
      .map(section => {
        const el = this._document.getElementById(section.id);
        if (!el) {
          return null;
        }

        return {
          id: section.id,
          top: el.getBoundingClientRect().top + defaultView.scrollY,
        };
      })
      .filter((section): section is { id: string; top: number } => section !== null);

    const documentHeight = this._document.documentElement.scrollHeight;
    this._sectionRanges = sectionStarts.map((section, index) => ({
      id: section.id,
      top: section.top,
      bottom: sectionStarts[index + 1]?.top ?? documentHeight,
    }));
  }

  private _shouldKeepActiveSection(scrollTop: number, viewportHeight: number): boolean {
    if (!this.activeSection) {
      return false;
    }

    const activeSectionRange = this._sectionRanges.find(section => section.id === this.activeSection);
    if (!activeSectionRange) {
      return false;
    }

    const stickyHeaderOffset = 80;
    const viewportTop = scrollTop + stickyHeaderOffset;
    const viewportBottom = scrollTop + viewportHeight;

    return activeSectionRange.bottom > viewportTop && activeSectionRange.top < viewportBottom;
  }

  private _getNextActiveSection(scrollTop: number, viewportHeight: number): string {
    if (!this.activeSection) {
      return '';
    }

    if (this._pendingActiveSectionId === this.activeSection) {
      if (this._shouldKeepActiveSection(scrollTop, viewportHeight)) {
        this._pendingActiveSectionId = '';
      }

      return this.activeSection;
    }

    return this._shouldKeepActiveSection(scrollTop, viewportHeight) ? this.activeSection : '';
  }

  private _scheduleViewportStateUpdate(): void {
    const defaultView = this._document.defaultView;
    if (!defaultView || this._animationFrameId !== null) {
      return;
    }

    this._animationFrameId = defaultView.requestAnimationFrame(() => {
      this._animationFrameId = null;
      this._updateViewportState();
    });
  }

  private _detachViewportListeners(): void {
    const defaultView = this._document.defaultView;
    if (defaultView && this._isScrollListenerAttached) {
      defaultView.removeEventListener('scroll', this._scrollHandler);
      defaultView.removeEventListener('resize', this._resizeHandler);
    }

    this._isScrollListenerAttached = false;
  }

  private _stripLastUpdatedLabel(lastUpdated: string): string {
    return lastUpdated
      .replace(/^Last updated:\s*/i, '')
      .replace(/^آخر تحديث:\s*/i, '')
      .trim();
  }
}
